import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { onboarding as onboardingApi } from '@/api/onboarding';
import { useAuth } from '@/auth/AuthContext';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';

/**
 * GitHub redirects here after the App is installed (the Setup URL is either this React route
 * directly, or the backend forwarder at /api/github/setup which redirects here).
 *
 * The flow:
 *   1. Read installation_id / setup_action / state from the URL.
 *   2. If we don't know who the user is yet, redirect to /signup?installationId=… — the
 *      installer becomes the OWNER as part of registration.
 *   3. Once authed, POST /api/onboarding/complete to bind the installation to the user's
 *      account.
 *   4. Send them to the dashboard for the newly-created org.
 */
export default function OnboardingCallbackPage() {
  // Call all hooks unconditionally first, then branch on state in render.
  const [params] = useSearchParams();
  const { user, isLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const ranRef = useRef(false);

  const installationIdRaw = params.get('installation_id');
  const setupAction = params.get('setup_action') ?? undefined;
  const state = params.get('state') ?? undefined;

  useEffect(() => {
    if (isLoading || !user || ranRef.current) return;
    if (!installationIdRaw) return;
    ranRef.current = true;
    const installationId = Number(installationIdRaw);
    (async () => {
      try {
        const result = await onboardingApi.complete(installationId, setupAction, state);
        await refresh();
        navigate(`/dashboard?organizationId=${result.organizationId}`, { replace: true });
      } catch (err) {
        setError(err);
      }
    })();
  }, [isLoading, user, installationIdRaw, setupAction, state, refresh, navigate]);

  // setup_action=request fires when the installer doesn't have permission to install themselves;
  // GitHub sends a "request submitted" view instead of completing.
  if (setupAction === 'request') {
    return <Navigate to="/onboarding/error?reason=request_pending" replace />;
  }

  if (!installationIdRaw) {
    return <Navigate to="/onboarding/error?reason=missing_installation_id" replace />;
  }

  // Not signed in → kick off the installer signup flow.
  if (!isLoading && !user) {
    return (
      <Navigate
        to={`/signup?installationId=${encodeURIComponent(installationIdRaw)}`}
        replace
      />
    );
  }

  if (error) {
    return (
      <div className="centered-full">
        <div className="card" style={{ maxWidth: 520 }}>
          <h2>We couldn&apos;t finish onboarding</h2>
          <ErrorBanner error={error} />
          <button className="btn" onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="centered-full">
      <Spinner label="Linking your GitHub installation…" />
    </div>
  );
}
